import React, { useCallback, useEffect, useState } from "react";

import { useAddOrUpdateDigitalAgronomistPreferences, useCancelAsyncOnUnmount, useGetDigitalAgronomistPreferences, useTranslation, useUserContext } from "utils";

import {
  CropRecommendationResponsePreference,
  INVESTMENT_LEVEL,
  NavigationSplitViewGroupType,
  NavigationSplitViewOnTileClickedProps,
  PH_TYPE,
  SOIL_TYPE,
} from "models";

import { Form, NavigationSplitView } from "components";

import OnboardingStepFormItem from "pages/app/digital-agronomist/components/OnboardingStepFormItem.tsx";
import {
  EditPreferencesFormValues,
  FORM_ITEM_TYPE,
  OnboardingFormValues,
  convertStepValueToLabel,
  generatePreferenceRequestBody,
  getValueTranslationKey,
  joinStringValues,
} from "pages/app/digital-agronomist/utils.tsx";

export const UpdateDigitalAgronomistPreferencesForm = ({
  selectedPreferencePosition,
  onFinish,
}: {
  selectedPreferencePosition: NavigationSplitViewOnTileClickedProps;
  onFinish: () => void;
}) => {
  const { t } = useTranslation("digitalAgronomist");
  const signal = useCancelAsyncOnUnmount()();
  const { user } = useUserContext();
  const { data } = useGetDigitalAgronomistPreferences({ user });
  const { mutate: addOrUpdateDigitalAgronomistPreferences, isSuccess: updateIsSuccess } = useAddOrUpdateDigitalAgronomistPreferences();
  const [activeTileIndex, setActiveTileIndex] = useState<NavigationSplitViewOnTileClickedProps>(selectedPreferencePosition);

  const handleNextStep = () => {
    if (!data) return;

    const formValues = form.getFieldsValue(true);
    const preferenceRequestBody = generatePreferenceRequestBody(formValues);
    addOrUpdateDigitalAgronomistPreferences({ requestBody: preferenceRequestBody, signal });
  };

  const [tilesGroups, setTilesGroups] = useState<NavigationSplitViewGroupType[]>(generateTilesGroups(t, handleNextStep, data?.preferences));

  const getTileIndexByName = useCallback(
    (fieldName: keyof EditPreferencesFormValues): NavigationSplitViewOnTileClickedProps | null => {
      const groupIndex = tilesGroups.findIndex((group) => group.subGroups.some((subGroup) => subGroup.tiles.some((tile) => tile.name === fieldName)));
      if (groupIndex === -1) return null;

      const subGroupIndex = tilesGroups[groupIndex].subGroups.findIndex((subGroup) => subGroup.tiles.some((tile) => tile.name === fieldName));
      if (subGroupIndex === -1) return null;

      const tileIndex = tilesGroups[groupIndex].subGroups[subGroupIndex].tiles.findIndex((tile) => tile.name === fieldName);
      if (tileIndex === -1) return null;

      return { groupIndex, subGroupIndex, tileIndex };
    },
    [tilesGroups]
  );

  const updateTile = useCallback(
    (tilesGroups: NavigationSplitViewGroupType[], fieldName: keyof EditPreferencesFormValues, value?: OnboardingFormValues[keyof OnboardingFormValues]) => {
      if (!fieldName) return tilesGroups;

      const groupTileIndex = getTileIndexByName(fieldName);
      if (!groupTileIndex) return tilesGroups;

      const { groupIndex, subGroupIndex, tileIndex } = groupTileIndex;
      const tileToUpdate = tilesGroups[groupIndex].subGroups[subGroupIndex].tiles[tileIndex];
      const translationKey = getValueTranslationKey(fieldName, value, updatePreferencesSteps);

      const updatedTile = {
        ...tileToUpdate,
        value: value !== undefined ? convertStepValueToLabel(t, fieldName, value, translationKey) : tileToUpdate.value,
        show: true,
      };

      const updatedTiles = [...tilesGroups];
      updatedTiles[groupIndex].subGroups[subGroupIndex].tiles[tileIndex] = updatedTile;
      return updatedTiles;
    },
    [getTileIndexByName, t]
  );

  const [form] = Form.useForm();

  const updateTiles = (changedValues: EditPreferencesFormValues) => {
    const fieldNameToUpdate = Object.keys(changedValues)[0] as keyof EditPreferencesFormValues;
    if (!fieldNameToUpdate) return;

    const formValues = form.getFieldsValue(true);
    const updatedValue = formValues[fieldNameToUpdate];
    const updatedTiles = updateTile(tilesGroups, fieldNameToUpdate, updatedValue);
    setTilesGroups(updatedTiles);
  };

  useEffect(() => {
    if (updateIsSuccess) {
      onFinish();
    }
  }, [updateIsSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Form
      className="onboarding-form"
      name="digital-agronomist-onboarding-form"
      form={form}
      initialValues={data?.preferences}
      onValuesChange={(changedValues: EditPreferencesFormValues) => updateTiles(changedValues)}
    >
      <NavigationSplitView
        groups={tilesGroups}
        activeTileIndex={activeTileIndex}
        setActiveTileIndex={setActiveTileIndex}
        title={t("update.editFarm")}
        height="43.75rem"
      />
    </Form>
  );
};

enum GROUP_NAME {
  FARM_PROFILE = "farmProfile",
  CROP_ROTATION_PREFERENCES = "cropRotationPreferences",
}

export type UpdatePreferencesStep = {
  name: keyof EditPreferencesFormValues;
  type: FORM_ITEM_TYPE;
  options: {
    translationKey: string;
    value: string | number | boolean;
    prefix?: string;
    suffix?: string;
  }[];
  group: GROUP_NAME;
  show?: boolean;
};

const updatePreferencesSteps: UpdatePreferencesStep[] = [
  {
    name: "isDrought",
    type: FORM_ITEM_TYPE.RADIO,
    options: [
      { translationKey: "yes", value: true },
      { translationKey: "no", value: false },
    ],
    group: GROUP_NAME.FARM_PROFILE,
    show: true,
  },
  {
    name: "pHType",
    type: FORM_ITEM_TYPE.RADIO,
    options: [
      { translationKey: "acidic", value: PH_TYPE.ACIDIC },
      { translationKey: "neutral", value: PH_TYPE.NEUTRAL },
      { translationKey: "alkaline", value: PH_TYPE.ALKALINE },
    ],
    group: GROUP_NAME.FARM_PROFILE,
    show: true,
  },
  {
    name: "soilType",
    type: FORM_ITEM_TYPE.RADIO,
    options: [
      { translationKey: "heavy", value: SOIL_TYPE.HEAVY },
      { translationKey: "medium", value: SOIL_TYPE.MEDIUM },
      { translationKey: "light", value: SOIL_TYPE.LIGHT },
    ],
    group: GROUP_NAME.FARM_PROFILE,
    show: true,
  },
  {
    name: "legumeBreaksYears",
    type: FORM_ITEM_TYPE.RADIO,
    options: [
      { translationKey: "notApplicable", value: 0 },
      { translationKey: "year", value: 1, prefix: "≤ 1" },
      { translationKey: "years", value: 2, prefix: "2" },
      { translationKey: "years", value: 3, prefix: "3" },
      { translationKey: "years", value: 4, prefix: "4" },
      { translationKey: "years", value: 5, prefix: "≥ 5" },
    ],
    group: GROUP_NAME.CROP_ROTATION_PREFERENCES,
    show: true,
  },
  {
    name: "investmentLevel",
    type: FORM_ITEM_TYPE.RADIO,
    options: [
      {
        translationKey: "lowAmount",
        value: INVESTMENT_LEVEL.LOW,
      },
      {
        translationKey: "mediumAmount",
        value: INVESTMENT_LEVEL.MEDIUM,
      },
      {
        translationKey: "highAmount",
        value: INVESTMENT_LEVEL.HIGH,
      },
    ],
    group: GROUP_NAME.CROP_ROTATION_PREFERENCES,
    show: true,
  },
  {
    name: "isFodder",
    type: FORM_ITEM_TYPE.RADIO,
    options: [
      { translationKey: "yes", value: true },
      { translationKey: "no", value: false },
    ],
    group: GROUP_NAME.CROP_ROTATION_PREFERENCES,
    show: true,
  },
  {
    name: "isBiogas",
    type: FORM_ITEM_TYPE.RADIO,
    options: [
      { translationKey: "yes", value: true },
      { translationKey: "no", value: false },
    ],
    group: GROUP_NAME.CROP_ROTATION_PREFERENCES,
    show: true,
  },
];

const generateTile = (
  step: UpdatePreferencesStep,
  t: (key: string) => string,
  handleNextStep: (fieldName: keyof EditPreferencesFormValues | keyof OnboardingFormValues) => void,
  preferences: CropRecommendationResponsePreference
) => {
  const fieldName = step.name;
  const title = t(`onboarding.steps.${fieldName}.shortTitle`);
  const ctaButtonLabel = t("onboarding.save");
  const options = step.options.map((option) => ({
    label: joinStringValues([option.prefix || "", t(`onboarding.options.${option.translationKey}`), option.suffix || ""]),
    value: option.value,
  }));
  const subtitle = t(`onboarding.steps.${step.name}.subtitle`) === "{{subtitlePlaceholder}}" ? undefined : t(`onboarding.steps.${step.name}.subtitle`);
  const translationKey = getValueTranslationKey(fieldName, preferences[step.name]!, updatePreferencesSteps);
  const value = convertStepValueToLabel(t, fieldName, preferences[step.name]!, translationKey);

  return {
    title: title,
    name: step.name,
    value: value,
    show: step.show || false,
    groupKey: step.group,
    main: (
      <OnboardingStepFormItem
        fieldName={step.name}
        title={t(`onboarding.steps.${fieldName}.title`)}
        type={step.type}
        t={t}
        ctaButtonLabel={ctaButtonLabel}
        options={options}
        inputSelectLabel={t(`onboarding.steps.${fieldName}.shortTitle`)}
        handleNextStep={handleNextStep}
        subtitle={subtitle}
        showProgressBar={false}
        isOnboardingStep={false}
      />
    ),
  };
};

const generateTilesGroups = (
  t: (key: string) => string,
  handleNextStep: (fieldName: keyof OnboardingFormValues | keyof EditPreferencesFormValues) => void,
  preferences?: CropRecommendationResponsePreference
): NavigationSplitViewGroupType[] => {
  if (!preferences) return [];
  const tiles = updatePreferencesSteps.map((step) => generateTile(step, t, handleNextStep, preferences));

  return [
    {
      subGroups: [
        { subtitle: t("onboarding.farmProfile"), tiles: tiles.filter((tile) => tile.groupKey === GROUP_NAME.FARM_PROFILE) },
        {
          subtitle: t("update.cropRotationPreferences"),
          tiles: tiles.filter((tile) => tile.groupKey === GROUP_NAME.CROP_ROTATION_PREFERENCES),
        },
      ],
    },
  ];
};
